import {SubmissionError} from 'redux-form/lib/SubmissionError'
import {call, put, select, takeLatest} from 'redux-saga/effects'

import {PRECONDITION_UNMET, isBadResponse, isGoodResponse} from '../../../api/statusCodes'
import {getShippingAddress, saveShippingAddress} from '../../../api/checkout'
import {formatAddressValues} from '../../../reducers/checkout/helpers'
import {validateAddressWorker} from '../../../sagas/checkout/shipping'
import {
  openAddressUpdateErrorModal,
  openErrorHelpCenterModal,
} from '../../../actions/userInteractions'
import {formatShippingAddressPayloadForServer} from '../../../sagas/checkout/helpers'
import {getShippingFirstName, getShippingLastName} from './selectors'
import * as shippingAddressActions from './actions'
import {checkoutApiError, GET_SHIPPING, SAVE_SHIPPING} from '../../../checkoutErrorCodes'
import {AddressUpdateAction} from './types'

export function* getShippingAddressWorker({payload: {orderId}}) {
  yield put(shippingAddressActions.getShippingAddressPending())

  try {
    const shippingAddressResponse = yield call(getShippingAddress, orderId)
    const {status} = shippingAddressResponse
    const shippingResponsePayload = formatAddressValues('shipping', shippingAddressResponse.json)

    if (isGoodResponse(status)) {
      yield put(shippingAddressActions.getShippingAddressSuccess(shippingResponsePayload))
    } else if (isBadResponse(status)) {
      throw checkoutApiError(GET_SHIPPING, status)
    } else {
      yield put(shippingAddressActions.getShippingAddressFailure())
    }
  } catch (e) {
    yield put(shippingAddressActions.getShippingAddressFailure(e))
  }
}

export function* getShippingAddressForUpdateSaga() {
  yield takeLatest([AddressUpdateAction.GET_SHIPPING_ADDRESS], getShippingAddressWorker)
}

export function* saveShippingAddressWorker({
  payload: {fields, resolve, reject, shippingOrderId, onSuccess},
}) {
  yield put(shippingAddressActions.saveShippingAddressPending())
  const addressToSave = yield call(validateAddressWorker, fields)
  if (!addressToSave) {
    yield put(shippingAddressActions.saveShippingAddressFailure())
    yield call(reject, new SubmissionError({}))
    return
  }
  // firstName and lastName have to be added manually from the store since we don't
  // expose them in the form and we don't allow users to update shipping first and last name
  const [firstName, lastName] = [
    yield select(getShippingFirstName),
    yield select(getShippingLastName),
  ]
  addressToSave.firstName = firstName
  addressToSave.lastName = lastName

  const orderId = shippingOrderId
  let json
  let status
  try {
    ;({json, status} = yield call(
      saveShippingAddress,
      orderId,
      formatShippingAddressPayloadForServer(addressToSave),
    ))
    if (isBadResponse(status)) {
      throw new Error()
    }
    yield call(resolve)
    yield put(
      shippingAddressActions.saveShippingAddressSuccess(formatAddressValues('shipping', json)),
    )
    yield onSuccess()
  } catch {
    if (status === PRECONDITION_UNMET) {
      yield put(openAddressUpdateErrorModal())
    }
    if (status !== PRECONDITION_UNMET) {
      yield put(openErrorHelpCenterModal())
    }
    yield put(
      shippingAddressActions.saveShippingAddressFailure(checkoutApiError(SAVE_SHIPPING, status)),
    )
    yield call(reject, new SubmissionError({}))
  }
}

export function* saveShippingAddressForUpdateSaga() {
  yield takeLatest([AddressUpdateAction.SAVE_SHIPPING_ADDRESS], saveShippingAddressWorker)
}
